/**
 * Country-specific investment schemes, government products, and regional
 * asset types that Muslim savers commonly hold and often forget to include
 * in their Zakat base.
 *
 * These field definitions plug into the generic asset-field renderer
 * (same shape as `assetFields` in `zakatConfig.js`): each entry has
 *   {
 *     label, type, category, tooltip,
 *     zakatable: true|false,   // default true; false = informational only
 *     school?: 'shafii' | 'hanafi' | ...  // if scholarly-opinion split
 *   }
 *
 * The `COUNTRY_SPECIFIC_ASSETS` map keys are ISO 2-letter country codes.
 * The calculator reads `selectedCountry.code` and merges the corresponding
 * fields into the visible asset list — so a user in India sees PPF, EPF, NPS,
 * SGB fields; a user in the UK sees ISA, SIPP, Premium Bonds; a user in
 * Malaysia sees EPF, ASNB, Tabung Haji; etc.
 *
 * Editorial rules:
 *   • Zakatability follows AAOIFI / AMJA / ECFR / JAKIM contemporary
 *     positions where they exist. For unresolved scholarly splits we
 *     default to the more common Sunni-majority position and flag it
 *     in the tooltip.
 *   • Retirement schemes that are LOCKED and INACCESSIBLE default to
 *     zakatable:false (per AMJA/ECFR). Users can override in the wizard.
 *   • Government prize-bond schemes: prize element is haram in classical
 *     fiqh; face value is zakatable, prizes should be purified.
 *   • This list is EDUCATIONAL — always confirm with a local scholar.
 */

const asset = (label, tooltip, opts = {}) => ({
  label,
  type: 'currency',
  category: opts.category || 'investments',
  tooltip,
  zakatable: opts.zakatable !== false,
  ...opts,
});

// -------------------------------------------------------------------------
export const COUNTRY_SPECIFIC_ASSETS = {

  // ==================== SOUTH ASIA =====================================
  IN: {
    ppf: asset('PPF (Public Provident Fund)',
      'Balance in your Public Provident Fund account. Locked for 15 years but partially withdrawable after year 6. Most Indian scholars (Jamiat Ulama-i-Hind, AIMPLB) treat the ACCESSIBLE portion as zakatable; the locked portion depends on your madhhab.',
      { category: 'retirement' }),
    epfIn: asset('EPF (Employees Provident Fund)',
      'Your accumulated EPF balance. AMJA-style position: not zakatable annually until you can withdraw without penalty. Cautious view: 2.5% on vested employee contribution.',
      { category: 'retirement', zakatable: false }),
    nps: asset('NPS (National Pension System)',
      'Locked till age 60. Contemporary Indian scholars generally treat as non-zakatable until vested/withdrawable.',
      { category: 'retirement', zakatable: false }),
    elss: asset('ELSS / Tax-Saver Mutual Funds',
      'Equity Linked Savings Schemes have a 3-year lock-in. Value at hawl-date NAV; 2.5% applies. Check the fund\'s halal-compliance certification.'),
    sgb: asset('Sovereign Gold Bonds (SGB)',
      'RBI-issued sovereign gold bonds. Treat like physical gold: 2.5% of market value at hawl date. See our Nisab guide for the silver-vs-gold benchmark.'),
    sukanya: asset('Sukanya Samriddhi Yojana',
      'Girl-child savings scheme. Balance is zakatable at the current 2.5% rate on the accessible principal + accrued interest (purify interest per Hanafi/Shafi\'i approach).'),
    nsc: asset('NSC / Kisan Vikas Patra / Post Office',
      'National Savings Certificate, KVP, and post-office savings. Face value + accrued interest zakatable; purify interest portion separately if concerned.'),
    ppfMinors: asset('PPF for Minors (guardian-held)',
      'PPF opened in the name of a minor child. Legally the minor\'s asset; the guardian pays Zakat on behalf of the minor once above nisab.',
      { category: 'retirement' }),
  },

  // ---------------------------------------------------------------------
  PK: {
    behbood: asset('Behbood Savings Certificate',
      'Fixed-return government savings scheme. Face value is zakatable; return element is riba — purify by separate donation, not from your Zakat.'),
    ric: asset('Regular Income Certificate (RIC)',
      'Ten-year government income certificate. Face value zakatable at hawl date.'),
    prizeBonds: asset('Prize Bonds (Face Value)',
      'Government prize bonds. Face value is zakatable capital; prize winnings are haram and must be donated separately.'),
    sanchay: asset('Sarmaya / Sarwar / Defence Savings',
      'Various DHA and government savings certificates. Include face value at hawl-date.'),
    nssCert: asset('Special Savings Certificate (SSC)',
      'Three-year government certificate. Face value zakatable.'),
    epfPk: asset('EOBI Pension Balance',
      'Employees\' Old-Age Benefits Institution contribution. Locked till retirement — most Pakistani scholars treat as non-zakatable until vested.',
      { category: 'retirement', zakatable: false }),
    islamicFunds: asset('Islamic Mutual Funds (Meezan / Al Meezan)',
      'Shariah-compliant fund units at hawl-date NAV. 2.5% applies.'),
  },

  // ---------------------------------------------------------------------
  BD: {
    dps: asset('DPS (Deposit Pension Scheme)',
      'Bank-run monthly deposit pension. Balance including profit is zakatable at hawl date.'),
    sanchaypatra: asset('Sanchay Patra (Savings Certificate)',
      'Government of Bangladesh savings certificate. Face value zakatable; profit element is contested by Bangladeshi scholars — many treat it as riba and purify.'),
    mfdr: asset('Islamic MFDR / Mudaraba Deposits',
      'Islamic bank Mudaraba fixed-deposit receipts. Balance + profit zakatable.'),
    epfBd: asset('Provident Fund Balance',
      'Employer-employee PF balance. Vested portion zakatable per contemporary councils; locked employer match not zakatable annually.',
      { category: 'retirement', zakatable: false }),
  },

  LK: {
    epfLk: asset('EPF / ETF (Sri Lanka)',
      'Employees\' Provident Fund + Employees\' Trust Fund. Locked till retirement — vested employee portion zakatable per Colombo scholars\' view.',
      { category: 'retirement', zakatable: false }),
    treasuryBills: asset('Treasury Bills / Bonds',
      'Government T-bills. Face value zakatable; interest coupon should be purified separately.'),
  },

  // ==================== SOUTHEAST ASIA =================================
  MY: {
    epfMy: asset('EPF (KWSP) Balance',
      'Employees Provident Fund total balance. JAKIM position: withdrawable portion (Account 2 + 3) is zakatable. Locked Account 1 (retirement) — some scholars require annual 2.5%, others defer until 55. Malaysia states may also collect Zakat directly.',
      { category: 'retirement' }),
    tabungHaji: asset('Tabung Haji (Pilgrimage Fund)',
      'Your Tabung Haji balance. Fully zakatable at hawl-date since it earns a dividend which is Shariah-compliant.'),
    asnb: asset('ASNB Unit Trusts (ASB, ASM, ASN)',
      'Amanah Saham Nasional Berhad units. Market NAV × 2.5% at hawl date. ASB2 and ASN are Shariah-compliant; ASB conventional needs purification of prohibited-sector income.'),
    prs: asset('Private Retirement Scheme (PRS)',
      'PRS balance — 30% withdrawable at 55, 100% at 60. Contemporary Malaysian scholars split: vested portion zakatable, locked portion depends on scholar.',
      { category: 'retirement' }),
    sukukMy: asset('Sukuk (Islamic Bonds)',
      'Malaysia is the world\'s largest sukuk market. Face value + accrued profit zakatable.'),
    asnbShariah: asset('ASNB Shariah Funds (ASBI, ASN Sukuk)',
      'Fully Shariah-compliant ASNB variants — NAV × 2.5% at hawl date.'),
  },

  ID: {
    reksadanaSyariah: asset('Reksa Dana Syariah',
      'Indonesian Shariah-compliant mutual funds. NAV × 2.5% at hawl date.'),
    sukukId: asset('Sukuk Ritel / Sukuk Tabungan',
      'Government retail sukuk. Face value + rentals zakatable.'),
    tabunganHaji: asset('Tabungan Haji / BPKH',
      'Hajj savings account. Balance zakatable at hawl date; upcoming Hajj expenses can be deducted only if imminent (within a year).'),
    dpk: asset('DPLK / BPJS-TK Balance',
      'DPLK pension + BPJS-TK worker social security. Vested portion zakatable; locked portion may be deferred.',
      { category: 'retirement', zakatable: false }),
  },

  SG: {
    cpf: asset('CPF Ordinary + Special + Medisave',
      'Central Provident Fund. MUIS position: OA (Ordinary Account) is zakatable if accessible (housing purchases); SA + MA (Special + Medisave) are locked — most scholars defer.',
      { category: 'retirement' }),
    srs: asset('SRS (Supplementary Retirement Scheme)',
      'Voluntary retirement account. Locked until 62 with 5% penalty for early withdrawal. Follow the retirement-account rule.',
      { category: 'retirement', zakatable: false }),
    cpfInvest: asset('CPF Investment Scheme (CPFIS)',
      'Investments held via CPFIS. Market value zakatable if underlying fund is halal.'),
  },

  BN: {
    tap: asset('TAP (Employees Trust Fund)',
      'Tabung Amanah Pekerja. Locked till retirement — vested contributions zakatable per Brunei State Mufti Department.',
      { category: 'retirement' }),
    sukukBn: asset('Brunei Sukuk',
      'Government retail sukuk. Face value + rentals zakatable.'),
  },

  // ==================== GCC =============================================
  SA: {
    gosi: asset('GOSI Balance (Retirement)',
      'General Organization for Social Insurance. State-managed scheme, not individually owned — typically not counted as personal zakatable wealth.',
      { category: 'retirement', zakatable: false }),
    tadawul: asset('Tadawul Stock Portfolio',
      'Saudi Exchange holdings. Market value × 2.5% at hawl date; use look-through for individual company halal filter.'),
    sukukSa: asset('Saudi Government Sukuk',
      'SA sovereign sukuk. Face value + coupons zakatable.'),
    reitsSa: asset('Saudi REITs',
      'Real estate investment trusts on Tadawul. Market value zakatable at hawl date.'),
  },

  AE: {
    difcPension: asset('DIFC Employee Workplace Savings (DEWS)',
      'DIFC end-of-service savings scheme. Vested contribution zakatable per AAOIFI standard.',
      { category: 'retirement' }),
    endServiceGratuity: asset('End-of-Service Gratuity (Payable)',
      'UAE end-of-service benefit. Zakatable only if you would receive it now on termination.'),
    dfmTadawul: asset('DFM / ADX Stock Portfolio',
      'Dubai + Abu Dhabi listed holdings. Market value × 2.5%.'),
  },

  QA: {
    qsePortfolio: asset('QSE Stock Portfolio',
      'Qatar Stock Exchange holdings. Market value × 2.5%.'),
    qatarPension: asset('Qatar Pension Fund',
      'General Retirement + Social Insurance Authority balance. Follow retirement rules.',
      { category: 'retirement', zakatable: false }),
  },

  KW: {
    kwSocial: asset('PIFSS Pension Balance',
      'Public Institution for Social Security. State scheme — typically not personally zakatable.',
      { category: 'retirement', zakatable: false }),
    bourse: asset('Boursa Kuwait Portfolio',
      'Kuwait stock exchange holdings. Market value × 2.5%.'),
  },

  BH: {
    gosiBh: asset('SIO (Social Insurance) Balance',
      'Bahrain Social Insurance Organisation. State-managed — not personally zakatable.',
      { category: 'retirement', zakatable: false }),
  },

  OM: {
    pasi: asset('PASI Pension Contribution',
      'Public Authority for Social Insurance balance. State scheme.',
      { category: 'retirement', zakatable: false }),
  },

  // ==================== WESTERN + DIASPORA =============================
  GB: {
    isa: asset('ISA (Cash + S&S) Balance',
      'Individual Savings Account — fully accessible tax-free wrapper. 2.5% on hawl-date balance. If it holds haram sector equity, purify separately.'),
    sipp: asset('SIPP / Personal Pension',
      'Self-Invested Personal Pension. Locked till 55/57 — AMJA/ECFR position: not zakatable annually. Cautious view: 2.5% on halal-fund balance.',
      { category: 'retirement', zakatable: false }),
    lisa: asset('Lifetime ISA (LISA)',
      'LISA balance. Withdrawable for first-home or age 60 — treat like an accessible ISA for Zakat unless truly locked for you.'),
    premBonds: asset('Premium Bonds (Face Value)',
      'NS&I Premium Bonds. Face value zakatable; prize winnings are contested — many British scholars consider them permissible since no interest is guaranteed, others require purification.'),
    juniorISA: asset('Junior ISA (guardian-held)',
      'JISA opened for a minor. Legally the child\'s asset; guardian pays Zakat if the balance exceeds nisab.'),
    workplacePension: asset('Workplace Pension (Auto-Enrol)',
      'DC workplace pension. Vested + accessible portion zakatable; locked portion typically deferred until access at 55/57.',
      { category: 'retirement', zakatable: false }),
  },

  US: {
    fourOhOneK: asset('401(k) Balance',
      'Traditional or Roth 401(k). AMJA position: locked till 59½ — not zakatable annually; only pay Zakat on the accessible-after-penalty amount if you want to be cautious.',
      { category: 'retirement', zakatable: false }),
    iraRoth: asset('IRA / Roth IRA',
      'Individual Retirement Account balance. Same rule as 401(k): locked → typically not zakatable annually per AMJA.',
      { category: 'retirement', zakatable: false }),
    hsa: asset('HSA (Health Savings Account)',
      'HSA balance. Contemporary US scholars mostly say: zakatable only on the accessible portion (usable for medical expenses now).',
      { zakatable: true }),
    fiveTwoNine: asset('529 College Savings Plan',
      '529 plan for a child. Legally the beneficiary\'s asset; guardian pays Zakat if above nisab.'),
    seriesIBonds: asset('Series I / EE Savings Bonds',
      'US Treasury inflation-adjusted bonds. Face value zakatable; interest element should be purified per Sunni-Hanafi practice.'),
    tsp: asset('TSP (Thrift Savings Plan)',
      'Federal employees\' TSP. Locked till 59½ — retirement rule applies.',
      { category: 'retirement', zakatable: false }),
  },

  CA: {
    rrsp: asset('RRSP (Registered Retirement Savings)',
      'Locked-in tax-advantaged retirement account. Contemporary Canadian scholars mostly defer Zakat until withdrawal per AMJA.',
      { category: 'retirement', zakatable: false }),
    tfsa: asset('TFSA (Tax-Free Savings Account)',
      'Fully accessible tax-free wrapper. 2.5% on hawl-date balance.'),
    resp: asset('RESP (Registered Education Savings)',
      'Education plan for a child. Legally the beneficiary\'s asset when disbursed; guardian assesses Zakat by scholar\'s guidance.'),
  },

  AU: {
    super: asset('Superannuation Balance',
      'Preservation age lock (typically 60). AMJA/ECFR aligned position: not zakatable annually. Cautious view: 2.5% on employer-vested + halal-invested balance.',
      { category: 'retirement', zakatable: false }),
    smsf: asset('SMSF (Self-Managed Super Fund)',
      'Same rule as regular super — locked → deferred, unless you have already met a condition of release.',
      { category: 'retirement', zakatable: false }),
  },

  NZ: {
    kiwisaver: asset('KiwiSaver Balance',
      'Locked till 65 (with narrow exceptions). Retirement-account rule applies.',
      { category: 'retirement', zakatable: false }),
  },

  ZA: {
    rsaTfsa: asset('SA Tax-Free Savings Account',
      'ZAR 500k lifetime limit; fully accessible → 2.5% at hawl date.'),
    provFund: asset('Provident / Pension Fund',
      'Employer retirement fund. Locked till retirement — follow the retirement rule.',
      { category: 'retirement', zakatable: false }),
  },

  NG: {
    pmb: asset('PMS / RSA Balance',
      'Retirement Savings Account under Pension Reform Act 2014. Locked till retirement — vested contribution zakatable per some Nigerian scholars, deferred per others.',
      { category: 'retirement', zakatable: false }),
  },

  KE: {
    ke_nssf: asset('NSSF Kenya Balance',
      'National Social Security Fund. Locked till retirement — deferred per Nairobi scholars.',
      { category: 'retirement', zakatable: false }),
  },

  TZ: {
    tz_nssf: asset('NSSF Tanzania Balance',
      'Locked till retirement — deferred per Dar es Salaam scholars.',
      { category: 'retirement', zakatable: false }),
  },

  // ==================== EUROPE =========================================
  FR: {
    livretA: asset('Livret A / LDDS',
      'French regulated savings. Fully accessible → 2.5% at hawl-date balance. Interest is riba — purify separately.'),
    peaFR: asset('PEA (Plan d\'Épargne Actions)',
      'Equity savings plan. Locked 5 years for tax benefit but LEGALLY WITHDRAWABLE with tax penalty. Contemporary Muslim-French scholars: zakatable at market value.'),
    perFR: asset('PER (Plan d\'Épargne Retraite)',
      'Locked till retirement — retirement rule applies.',
      { category: 'retirement', zakatable: false }),
    assuranceVie: asset('Assurance-Vie',
      'Investment/insurance wrapper. Withdrawable with tax hit — zakatable per most contemporary councils.'),
  },

  DE: {
    riesterRente: asset('Riester / Rürup Rente',
      'German subsidised pension. Locked → retirement rule.',
      { category: 'retirement', zakatable: false }),
    bausparen: asset('Bausparvertrag',
      'Building society contract balance. Zakatable on capital; purify any interest element.'),
  },

  NL: {
    aow: asset('AOW / Employer Pension',
      'State + occupational pension. Locked → retirement rule.',
      { category: 'retirement', zakatable: false }),
  },

  BE: {
    epargnePensionBe: asset('Pension Savings Plan (3rd Pillar)',
      'Belgian 3rd-pillar pension savings. Locked till 60 — retirement rule.',
      { category: 'retirement', zakatable: false }),
  },

  ES: {
    planPensiones: asset('Plan de Pensiones',
      'Spanish personal pension plan. Locked till 65 (or 10 years). Retirement rule.',
      { category: 'retirement', zakatable: false }),
  },

  IT: {
    tfr: asset('TFR (Trattamento di Fine Rapporto)',
      'End-of-service severance held with employer. Zakatable when receivable, not annually while locked.',
      { category: 'retirement', zakatable: false }),
  },

  TR: {
    bes: asset('BES (Bireysel Emeklilik Sistemi)',
      'Individual Pension System balance. Locked till 56 — Turkish contemporary scholars (Diyanet) generally defer Zakat until vested.',
      { category: 'retirement', zakatable: false }),
    altınHesap: asset('Gold Account (Altın Hesabı)',
      'Bank-managed gold-denominated account. Treat exactly like physical gold: 2.5% of market value.'),
  },

  // ==================== CIS + CENTRAL ASIA =============================
  RU: {
    ipf: asset('Individual Investment Account (ИИС)',
      'Russian tax-advantaged brokerage. Withdrawable with tax hit — zakatable at market value.'),
    npf: asset('НПФ (Non-State Pension Fund)',
      'Locked till retirement — deferred.',
      { category: 'retirement', zakatable: false }),
  },
};

/**
 * Look up country-specific schemes for a country code.
 * Returns an object keyed by field-id (same shape as `assetFields`).
 * Returns an empty object for unmapped countries.
 */
export const getCountrySpecificAssets = (countryCode) => {
  if (!countryCode) return {};
  return COUNTRY_SPECIFIC_ASSETS[countryCode.toUpperCase()] || {};
};

/** How many countries currently have curated scheme lists. */
export const countryCoverageCount = Object.keys(COUNTRY_SPECIFIC_ASSETS).length;
